/**
 * Section 6: Video Submission and Property Feedback
 */

import { useCallback } from 'preact/hooks';
import { FileUpload } from '@ox/visits-ui/components/shared/FileUpload';
import { Textarea } from '@ox/visits-ui/components/shared/FormComponents';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { MIN_TOTAL_VIDEO_SECONDS, formatMinutes, useTotalVideoSeconds } from '@ox/visits-ui/hooks/useVideoDurations';

export function Section6VideoSubmission() {
  const { formData, updateField, viewMode, getMediaByType } = useForm();
  
  const handlePropertyCommentsChange = useCallback((value: string) => {
    updateField('property_comments', value);
  }, [updateField]);

  // Get uploaded videos
  const walkthroughVideos = getMediaByType(6, 'walkthrough_video');
  const hasVideo = walkthroughVideos.length > 0;
  // The CMA waits for 3 minutes of footage in total; several shorter clips count together.
  const totalSeconds = useTotalVideoSeconds(walkthroughVideos);
  
  return (
    <div className="form-section">
      <h2>Video Submission and Property Feedback</h2>

      <a href="/guidance?checklist=video" target="_blank" aria-label="Video Checklist"><h3><strong>🎥 Video Checklist</strong></h3></a>

      {viewMode && hasVideo ? (
        <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
          {walkthroughVideos.map((video) => (
            <video 
              key={video.id}
              controls 
              style={{ 
                width: '100%',
                maxWidth: '800px',
                display: 'block',
                margin: '5px auto'
              }}
            >
              <source src={video.signedUrl as unknown as string} type="video/mp4" />
              <source src={video.signedUrl as unknown as string} type="video/quicktime" />
              <track kind="captions" src={video.signedUrl as unknown as string} />
              Your browser does not support the video tag.
            </video>
          ))}
        </div>
      ) : (
        <FileUpload
          label="Submit Property Videos"
          mediaType="walkthrough_video"
          sectionNumber={6}
          maxFiles={20}
          accept="video/*"
          helpText="Film video walkthrough in its entirety, do not take partial videos"
          required
        />
      )}
      {!viewMode && hasVideo && (
        <div className="file-upload-help-text">
          {totalSeconds >= MIN_TOTAL_VIDEO_SECONDS ? <strong>√</strong> : '⚠️'} Total video:{' '}
          {formatMinutes(totalSeconds)} of {formatMinutes(MIN_TOTAL_VIDEO_SECONDS)} minimum
          {totalSeconds < MIN_TOTAL_VIDEO_SECONDS && ' — pricing can’t start the CMA until the walkthrough adds up to 3 minutes.'}
        </div>
      )}
      
      <Textarea
        label="Additional Property Comments or General Feedback?"
        name="property_comments"
        value={String(formData.property_comments ?? '')}
        onChange={handlePropertyCommentsChange}
        autoSize
        required
      />
    </div>
  );
}
